<template lang="">
  <BCard class="rounded-lg mb-0">
    <Stack class="gap-2">
      <h3 class="mb-0 fw-700">
        {{ $t('Flights Schedule') }}
      </h3>
      <FlightsScheduleFilters
        :departureFilter.sync="departureFilter"
        :departureDateFilter.sync="departureDateFilter"
        :tripTypeFilter.sync="tripTypeFilter"
        :stationTypeFilter.sync="stationTypeFilter"
        :departureHourFilter.sync="departureHourFilter"
      />

      <!-- ANCHOR Table List -->
      <b-table
        id="flights-schedule-table"
        ref="tableRef"
        :style="maxHeightStyle"
        sticky-header
        class="position-relative table-gray"
        :items="flightScheduleRender"
        responsive
        show-empty
        :empty-text="$t('noMatchingResult')"
        no-border-collapse
        bordered
        :small="isMobileView"
        hover
        :busy="loading"
        :fields="tableColumns"
        head-row-variant="green"
        :tbody-tr-class="(item) => resolveClassRow(item)"
        @row-clicked="onRowClicked"
      >
        <template
          v-for="column in tableColumns"
          #[`head(${column.key})`]="data"
        >
          <span
            :key="column.key"
            class="text-nowrap"
          >
            <div>{{ data.column === 'action' ? '' : $t(`utils.flightsSchedule.columns.${data.column}${data.column === 'airportName' ? tripTypeFilter : ''}`) }}</div>
          </span>
        </template>

        <template #table-busy>
          <div class="d-flex-center text-dark my-2 gap-2">
            <b-spinner class="align-middle" />
            <strong>{{ $t('loading') }}</strong>
          </div>
        </template>

        <template #cell(scheduleTime)="{ item }">
          <Stack
            center
            class="fw-600"
          >
            {{ item.scheduleTime }}
          </Stack>
        </template>

        <template #cell(estimateTime)="{ item }">
          <Stack
            center
            class="fw-600"
          >
            {{ item.estimateTime }}
          </Stack>
        </template>

        <template #cell(airportName)="{ item }">
          <span class="fw-600">
            {{ item.airportName }}
          </span>
        </template>

        <template #cell(airlineName)="{ item }">
          <HStack center>
            <IAmLogoAirline
              v-if="item.airline"
              :airline="item.airline"
              :longLogo="!isMobileView"
              size="sm"
              class="cursor-pointer"
            />
            <span
              v-if="!['VU', 'QH', 'VN', 'VJ', 'BL'].includes(item.airline) && !isMobileView"
              class="fw-600"
            >
              {{ item.airlineName }}
            </span>
          </HStack>
        </template>

        <template #cell(flightNumber)="{ item }">
          <HStack center>
            <IAmLogoAirline
              v-if="item.airline && isMobileView"
              :airline="item.airline"
              height="16"
              class="cursor-pointer"
            />
            <div class="w-100 fw-600">
              {{ item.flightNumber }}
            </div>
          </HStack>
        </template>

        <template #cell(gate)="{ item }">
          <span class="fw-600">
            {{ item.gate }}
          </span>
        </template>

        <template #cell(remark)="{ item }">
          <BBadge
            v-if="item.remark"
            :variant="resolveRemarkVariant?.[item.remark] || 'light-secondary'"
            pill
            class="px-75"
          >
            {{ $te(`utils.flightsSchedule.remark.${item.remark}`)
              ? $t(`utils.flightsSchedule.remark.${item.remark}`)
              : item.remark }}
          </BBadge>
        </template>

        <template #cell(action)="{ item }">
          <BButton
            variant="flat-dark"
            class="p-25 cursor-pointer rounded-circle"
            @click.stop.prevent="onRowClicked(item)"
          >
            <IAmIcon
              icon="chevronDownOutline"
              :class="`rotate-transition ${item._showDetails ? 'rotate-180' : ''}`"
            />
          </BButton>
        </template>

        <template #row-details="{ item }">
          <HStack
            v-if="isMobileView"
            class="gap-2"
          >
            <Stack>
              <div
                v-for="col in columnDetails"
                :key="col"
                class="fw-500 text-nowrap"
              >
                {{ $t(`utils.flightsSchedule.columns.${col}${col === 'airportName' ? tripTypeFilter : ''}`) }}:
              </div>
            </Stack>
            <Stack>
              <div
                v-for="col in columnDetails"
                :key="col"
                class="fw-600 text-nowrap"
              >
                {{ item[col] }}
              </div>
            </Stack>
          </HStack>
        </template>
      </b-table>
    </Stack>
  </BCard>
</template>
<script>
import {
  computed, onMounted, ref,
  watch,
} from '@vue/composition-api'
import {
  BBadge, BCard, BSpinner, BTable, BButton,
} from 'bootstrap-vue'
import { debouncedWatch } from '@vueuse/core'

import { apiUtil } from '@/api'
import { getToday } from '@/@core/utils/filter'

import useToast from '@useToast'

import {
  DEPARTURE, resolveRemarkVariant, STATION_TYPE, tableColumns as tableColumnsRaw, TRIP_TYPE,
} from './useFlightsSchedule'

export default {
  components: {
    BTable,
    BSpinner,
    BCard,
    BBadge,
    BButton,
    IAmLogoAirline: () => import('@/components/IAmLogoAirline.vue'),
    FlightsScheduleFilters: () => import('./FlightsScheduleFilters.vue'),
  },
  setup(_, { root }) {
    const { toastError } = useToast()

    const loading = ref(false)

    // FILTERS
    const departureFilter = ref(DEPARTURE.HAN)
    const departureDateFilter = ref(getToday('YYYY-MM-DD'))
    const tripTypeFilter = ref(TRIP_TYPE.Departure)
    const stationTypeFilter = ref(STATION_TYPE.DOM)
    const departureHourFilter = ref(null)

    const tableColumns = computed(() => root.isMobileView
      ? tableColumnsRaw.filter(col => !col.isHideMobile)
      : tableColumnsRaw.filter(col => !col.isOnlyShowMobile))

    const flightSchedule = ref([])
    const flightScheduleRender = computed(() => flightSchedule.value
      .filter(item => {
        if (departureHourFilter.value && item.estimateTime) {
          const [start, end] = departureHourFilter.value.split(' | ')
          const estHour = Number(item.estimateTime.split(':')?.[0])
          return estHour >= start && estHour < end
        }
        return true
      })
      .map(item => ({
        ...item,
        _showDetails: false,
        _cellVariants: { flightNumber: item.airline },
      })))

    async function fetchFlightSchedule() {
      loading.value = true
      try {
        const params = {
          departure: departureFilter.value,
          departureDate: departureDateFilter.value,
          tripType: tripTypeFilter.value,
          stationType: stationTypeFilter.value,
        }
        const res = await apiUtil.getFlightsSchedule(params)
        // filter chuyến `chater` (https://discord.com/channels/1054696448110903327/1359103172664230019/1359583709145075732)
        flightSchedule.value = res.data.items
          .filter(item => !['CH'].includes(item.airline))
      } catch (error) {
        toastError('')
      } finally {
        loading.value = false
      }
    }

    const tableRef = ref()
    const maxHeightStyle = computed(() => {
      const offsetTop = tableRef.value?.$el?.offsetTop || 0
      const rs = offsetTop > 0 ? { maxHeight: `calc(80vh - ${offsetTop + 20}px)` } : { maxHeight: '70vh' }
      return rs
    })

    function onRowClicked(item) {
      if (!root.isMobileView) return
      // eslint-disable-next-line no-underscore-dangle
      item._showDetails = !item._showDetails
    }

    watch(() => root.isMobileView, () => {
      if (!root.isMobileView) {
        flightScheduleRender.value.forEach(item => {
          // eslint-disable-next-line no-underscore-dangle
          item._showDetails = false
        })
      }
    })

    function resolveClassRow(item) {
      const { estimateTime, scheduleTime } = item ?? {}
      return `row-class ${estimateTime && scheduleTime && (estimateTime !== scheduleTime) ? 'text-danger row-caution' : 'text-dark'}`
    }

    debouncedWatch([
      departureFilter,
      departureDateFilter,
      tripTypeFilter,
      stationTypeFilter,
    ], () => {
      fetchFlightSchedule()
    }, { debounce: 500 })

    onMounted(() => {
      fetchFlightSchedule()
    })

    return {
      departureFilter,
      departureDateFilter,
      tripTypeFilter,
      stationTypeFilter,
      departureHourFilter,

      loading,
      flightScheduleRender,
      maxHeightStyle,
      tableRef,
      tableColumns,
      resolveRemarkVariant,
      onRowClicked,
      columnDetails: ['scheduleTime', 'airportName', 'airlineName', 'gate'],
      resolveClassRow,
    }
  },
}
</script>
<style lang="scss" scoped>
  ::v-deep {
     .table-green {
      background-color: #c8e3b4 !important;
      border-color: white !important;
      color: dark;
    }

    .row-class {
      background: #F3F3F3;
      &.row-caution {
        background: #fff0f1;
      }
    }

    .table-bordered th, .table-bordered td {
      border: 1px solid white !important;
      &.table- {
        &VN{
          background-color: #EAF6FC !important;
        }
        &QH {
          background-color: #EBF7EA !important;
        }
        &VJ {
          background-color: #FDE9EA !important;
        }
        &BL {
          background-color: #E6EEFA !important;
        }
        &VU {
          background-color: #FFF9E1 !important;
        }
      }
    }
  }
</style>
